'use client';
import { useEffect, useMemo, useState } from 'react';
import styles from './HeroVirtual.module.scss';
import Gallery from '@/components/ui/Gallery';
import type { HeroVirtualTypes } from './HeroVirtual.types';
import type { ImgType } from '@/global/types';
import { formatPrice } from '@/utils/price-formatter';
import AddToCart from '@/components/ui/AddToCart';
import { Hearth, PayPo } from '@/components/ui/Icons';
import Img from '@/components/ui/image';
import Button from '@/components/ui/Button';
import type { VideoProvider } from '@/components/ui/VideoPlayer/VideoPlayer.types';
import { getProductUserData } from '@/utils/user-actions';
import GiftPurchaseModal from './GiftPurchaseModal';

const gtag: Gtag.Gtag = function () {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
};

const HeroVirtual = ({ alreadyBought: initialAlreadyBought, course, previewLessons }: HeroVirtualTypes) => {
  // Fetch ownership client-side if not provided (allows static page rendering)
  const [alreadyBought, setAlreadyBought] = useState(initialAlreadyBought ?? false);
  const [ownershipLoading, setOwnershipLoading] = useState(initialAlreadyBought === undefined);
  const [ownedCourses, setOwnedCourses] = useState<string[]>([]);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  useEffect(() => {
    getProductUserData()
      .then((data) => {
        setOwnedCourses(data.ownedCourses);
        if (initialAlreadyBought === undefined && data.ownedCourses.includes(course._id)) {
          setAlreadyBought(true);
        }
      })
      .finally(() => setOwnershipLoading(false));
  }, [initialAlreadyBought, course._id]);
  const images = useMemo(() => {
    const images: Array<{
      data: ImgType | string;
      type: 'video' | 'image';
      videoProvider?: VideoProvider;
      libraryId?: string;
      libraryApiKey?: string;
    }> = [];
    // add video as first element if exists
    if (course?.featuredVideo) {
      images.push({
        type: 'video',
        data: course.featuredVideo,
        videoProvider: course.videoProvider || 'vimeo',
        libraryId: course.libraryId,
        libraryApiKey: course.libraryApiKey,
      });
    }
    course?.gallery?.forEach((el) => el && images.push({ type: 'image', data: el }));
    return images;
  }, [course]);

  useEffect(() => {
    const product_id = course._id;
    const product_name = course.name;
    const product_price = course.price! / 100;

    const timeoutId = setTimeout(() => {
      if (typeof fbq !== 'undefined') {
        fbq('track', 'ViewContent', {
          content_ids: [product_id],
          content_name: product_name,
          contents: [
            {
              id: product_id,
              item_price: product_price,
              quantity: 1,
            },
          ],
          content_type: 'product',
          value: product_price,
          currency: 'PLN',
        });
      }
      gtag('event', 'view_item', {
        currency: 'PLN',
        value: course.discount ? course.discount / 100 : course.price! / 100,
        items: [
          {
            id: product_id,
            name: product_name,
            discount: course.discount ? (course.price! - course.discount) / 100 : undefined,
            price: product_price,
            item_category: course.type,
            item_category2: course.basis,
          },
        ],
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [course]);

  return (
    <section className={styles['HeroVirtual']}>
      <Gallery images={images} />
      <div className={styles['info']}>
        {course.rating !== undefined && course.reviews?.length > 0 ? (
          <p className={styles['rating']}>
            <Hearth />{' '}
            <span>
              <b>{course.rating}</b>/5 ({course.reviews?.length})
            </span>
          </p>
        ) : (
          <p className={styles['rating']}>
            <Hearth /> <span>Brak opinii</span>
          </p>
        )}
        <h1>{course.name}</h1>
        <div className={styles['flex']}>
          <div className={styles['price']}>
            <p>
              <span className={course!.discount ? styles['discount'] : ''}>{formatPrice(course.price!)}</span>{' '}
              {course!.discount && <span>{formatPrice(course.discount)}</span>}
            </p>
            <small>Najniższa cena z 30 dni przed obniżką: {formatPrice(course.price!)}</small>
          </div>
        </div>
        <div className={`${styles['flex']} ${ownershipLoading ? styles['actions-loading'] : ''}`}>
          {alreadyBought ? (
            <Button href={`/moje-konto/kursy/${course.slug}`}>Przejdź do kursu</Button>
          ) : (
            <AddToCart
              data={{
                price: course.price!,
                discount: course.discount,
                _id: course._id,
                name: course.name,
                _type: course._type,
                basis: course.basis,
                courses: course.courses,
                allowPurchaseWithOwnedCourses: course.allowPurchaseWithOwnedCourses,
              }}
              ownedCourses={ownedCourses}
              id={course._id}
            />
          )}
          {course.giftPurchaseEnabled !== false && (
            <button
              type='button'
              className={styles['gift-button']}
              onClick={() => setIsGiftModalOpen(true)}
            >
              Kup jako prezent
            </button>
          )}
          {!alreadyBought && previewLessons?.slug && (
            <a
              className='link'
              href={`./${course.slug}/${previewLessons.slug}`}
            >
              Obejrzyj pierwszy moduł za darmo
            </a>
          )}
        </div>
        <p className={styles['pay-po']}>
          Kup dzisiaj i zapłać za 30 dni z PayPo
          <PayPo />
        </p>
        <div className={styles['author']}>
          <Img
            sizes={'60px'}
            data={course.author.image}
          />
          <div>
            <p>
              Autorka {course.author.countOfCourse} {course.author.countOfCourse === 1 ? 'kursu' : 'kursów'}
            </p>
            <p>{course.author.description}</p>
          </div>
        </div>
      </div>
      {isGiftModalOpen && (
        <GiftPurchaseModal
          course={course}
          onClose={() => setIsGiftModalOpen(false)}
        />
      )}
    </section>
  );
};

export default HeroVirtual;
